





















































import {Component, Prop, Vue} from "vue-property-decorator";
import {Slot} from "@/model/collection/Slot";
import {Drive} from "@/model/Drive";
import {Support, SUPPORT_STATUS_ORDER} from "@/model/support/Support";
import formatDate from '@/filters/DateFormatter';
import SupportPanel from "@/components/supports/SupportPanel.vue";
import {Inject} from "vue-typedi";
import {SupportService} from "@/services/SupportService";
import { SupportDisplayContexts, SupportDisplayOptions } from "@/model/support/SupportDisplayOptions";
import ScanDetector from "@/helpers/ScanDetector";
import ErrorDialog from "@/components/dialog-box/ErrorDialog.vue";

@Component({
  components: {ErrorDialog, SupportPanel },
  filters: { formatDate }
})
export default class DropOffWorkbench extends Vue {

  private static readonly LOCATION_ID_PATTERN: RegExp = /^[A-Za-z]{2}[0-9]{2}[A-Za-z][0-9]$/;

  @Prop({default: null})
  protected drive: Drive;
  @Prop({default: null})
  protected collectionSlot: Slot;
  @Prop({default: null})
  protected supportsToDropOff: Support[];

  @Inject()
  supportService!: SupportService;

  currentSupportId: string = null;
  currentLocationId: string = null;
  supportScanDetector: ScanDetector = new ScanDetector((value: string) => {
    this.currentSupportId = value;
    if (this.canLoadSupport) {
      this.loadSupport(this.currentSupportId);
    }
  });
  locationScanDetector: ScanDetector = new ScanDetector((value: string) => {
    this.currentLocationId = value;
    if (this.canAllocateLocation) {
      this.allocateLocation();
    }
  });
  selectedSupport: Support = null;
  isLoading: boolean = false;
  isListingSupports: boolean = false;
  error: DropOffWorkbenchError = null;

  mounted() {
    this.validateState(DropOffWorkbenchActions.INIT);
    this.focusInput();
  }

  beforeDestroy() {
    this.supportScanDetector.reset();
    this.locationScanDetector.reset();
  }

  back() {
    this.$emit('back');
  }

  onSupportScanInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value;
    if (value == null) {
      return;
    }
    this.supportScanDetector.onInput(value);
  }

  onLocationScanInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value;
    if (value == null) {
      return;
    }
    this.locationScanDetector.onInput(value);
  }

  loadSupport(id: string): void {
    this.selectedSupport = this.findSupport(id);
    this.validateState(DropOffWorkbenchActions.LOAD_SUPPORT);
    this.focusInput();
    if (this.selectedSupport != null) {
      this.currentSupportId = null;
    }
  }

  allocateLocation(): void {
    if (this.isLoading) {
      return;
    }

    this.currentLocationId = this.currentLocationId?.trim().toLocaleUpperCase() ?? null;
    this.validateState(DropOffWorkbenchActions.ALLOCATE_LOCATION);
    if (this.error != null) {
      this.focusInput();
      return;
    }

    this.selectedSupport.dropOffLocation = this.currentLocationId;
    this.selectedSupport.dropOffDate = new Date();
    this.isLoading = true;

    this.supportService
      .dropOffSupport(this.selectedSupport.code, this.selectedSupport.dropOffLocation)
      .then((updatedSupport: Support) => {
        const updatedList = this.supportsToDropOff.map(support =>
          support.code === updatedSupport.code
            ? updatedSupport
            : support
        );

        this.$emit('updateSupportsToDropOff', updatedList);
        return this.supportService.confirmDropOffSupport(updatedSupport);
      })
      .finally(() => {
        this.resetWorkbench();
      })
  }

  closeErrorPopUp(): void {
    this.error = null;
    this.currentSupportId = '';
    this.currentLocationId = '';
    this.focusInput();
  }

  focusInput() {
    this.$nextTick(() => {
      (this.$refs.supportInput as HTMLInputElement)?.focus();
    });
  }

  get supportsToDropOffSorted() {
    return [...this.supportsToDropOff].sort((a, b) =>
      SUPPORT_STATUS_ORDER[a.supportStatus] - SUPPORT_STATUS_ORDER[b.supportStatus]
    );
  }

  startListingSupports(): void {
    this.isListingSupports = true;
  }

  stopListingSupports(): void {
    this.isListingSupports = false;
    this.focusInput();
  }

  private validateState(action: DropOffWorkbenchAction): void {
    if (this.supportsToDropOff == null || this.supportsToDropOff.length === 0) {
      this.error = DropOffWorkbenchErrors.NO_SUPPORTS_TO_DROP_OFF;
      return;
    }

    if (action === DropOffWorkbenchActions.LOAD_SUPPORT) {
      if (this.currentSupportId == null || this.currentSupportId.trim() === '') {
        this.error = DropOffWorkbenchErrors.MISSING_SUPPORT_ID;
        return;
      }

      if (this.selectedSupport == null) {
        this.error = DropOffWorkbenchErrors.SUPPORT_ID_NOT_FOUND;
        return;
      }

      if (this.selectedSupport.dropOffLocation != null) {
        this.error = DropOffWorkbenchErrors.SUPPORT_ALREADY_DROPPED_OFF;
        this.selectedSupport = null;
        this.currentSupportId = null;
        return;
      }
    }

    if (action == DropOffWorkbenchActions.ALLOCATE_LOCATION) {
      if (this.currentLocationId == null || this.currentLocationId === '') {
        this.error = DropOffWorkbenchErrors.MISSING_LOCATION_ID;
        return;
      }

      if (this.selectedSupport != null && this.currentLocationId.toLocaleUpperCase() === this.selectedSupport.code.toLocaleUpperCase()) {
        this.error = DropOffWorkbenchErrors.LOCATION_ID_IS_SUPPORT_ID;
        return;
      }

      if (this.currentLocationId.length !== 6) {
        this.error = DropOffWorkbenchErrors.LOCATION_ID_INVALID_LENGTH;
        return;
      }

      if (!DropOffWorkbench.LOCATION_ID_PATTERN.test(this.currentLocationId)) {
        this.error = DropOffWorkbenchErrors.LOCATION_ID_INVALID_FORMAT;
        return;
      }
    }

    this.error = null;
  }

  private resetWorkbench(): void {
    this.currentSupportId = null;
    this.currentLocationId = null;
    this.supportScanDetector.reset();
    this.locationScanDetector.reset();
    this.selectedSupport = null;
    this.isLoading = false;
    this.error = null;
    this.focusInput();
  }

  private findSupport(code: string): Support {
    if (this.supportsToDropOff == null || code == null) {
      return null;
    }
    return this.supportsToDropOff.find(support => support.code.toLocaleUpperCase() === code.trim().toLocaleUpperCase());
  }

  get supportDisplayOptions(): SupportDisplayOptions {
    return {
      context: SupportDisplayContexts.DROP_OFF,
      requiresManualAction: false
    };
  }

  get slotDate(): Date {
    return this.collectionSlot ? this.collectionSlot.dueDate : null;
  }

  get droppedOffSupportsCount(): number {
    return this.supportsToDropOff.filter(supp => supp.dropOffLocation != null)
      .length;
  }

  get totalSupportsCount(): number {
    return this.supportsToDropOff.length;
  }

  get hasValidCurrentSupportId(): boolean {
    return this.currentSupportId != null && this.currentSupportId.trim() !== '';
  }

  get hasValidCurrentLocationId(): boolean {
    return this.currentLocationId != null && this.currentLocationId.trim() !== '';
  }

  get canLoadSupport(): boolean {
    return this.hasValidCurrentSupportId;
  }

  get canAllocateLocation(): boolean {
    return this.hasValidCurrentLocationId && !this.isLoading;
  }

  get canListSupports(): boolean {
    return this.supportsToDropOff != null && this.supportsToDropOff.length > 0;
  }
}

export enum DropOffWorkbenchActions {
  INIT = 'INIT',
  LOAD_SUPPORT = 'LOAD_SUPPORT',
  ALLOCATE_LOCATION = 'ALLOCATE_LOCATION'
}

export type DropOffWorkbenchAction = DropOffWorkbenchActions.INIT
  | DropOffWorkbenchActions.LOAD_SUPPORT
  | DropOffWorkbenchActions.ALLOCATE_LOCATION;

export enum DropOffWorkbenchErrors {
  NO_SUPPORTS_TO_DROP_OFF = 'L\'identifiant de support saisi est vide.',
  MISSING_SUPPORT_ID = 'Veuillez saisir un identifiant de support valide.',
  SUPPORT_ID_NOT_FOUND = 'L\'identifiant de support saisi ne fait pas partie de la collecte sélectionnée.',
  MISSING_LOCATION_ID = 'Veuillez saisir un identifiant d\'emplacement valide.',
  SUPPORT_ALREADY_DROPPED_OFF = 'Le support a déjà été déposé.',
  LOCATION_ID_IS_SUPPORT_ID = 'L\'emplacement scanné correspond au support en cours de traitement, veuillez scanner l\'emplacement de dépose.',
  LOCATION_ID_INVALID_LENGTH = 'L\'identifiant d\'emplacement doit contenir exactement 6 caractères.',
  LOCATION_ID_INVALID_FORMAT = 'L\'identifiant d\'emplacement doit respecter le format 2 lettres, 2 chiffres, 1 lettre, 1 chiffre (exemple : AB01C2).'
}

export type DropOffWorkbenchError = DropOffWorkbenchErrors.NO_SUPPORTS_TO_DROP_OFF
  | DropOffWorkbenchErrors.MISSING_SUPPORT_ID
  | DropOffWorkbenchErrors.SUPPORT_ID_NOT_FOUND
  | DropOffWorkbenchErrors.MISSING_LOCATION_ID
  | DropOffWorkbenchErrors.SUPPORT_ALREADY_DROPPED_OFF
  | DropOffWorkbenchErrors.LOCATION_ID_IS_SUPPORT_ID
  | DropOffWorkbenchErrors.LOCATION_ID_INVALID_LENGTH
  | DropOffWorkbenchErrors.LOCATION_ID_INVALID_FORMAT;
